import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Select,
  Spinner,
  Text,
  HStack,
  Badge,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  FormControl,
  FormLabel,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Button,
  ButtonGroup,
  RadioGroup,
  Radio,
  VStack,
} from "@chakra-ui/react";
import { MapContainer, TileLayer, Tooltip, Marker } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/map.css";
import axiosInstance from "../api/axiosConfig";

const TRAFFIC_RANGES = {
  ALL: { min: 1, label: "Todos", color: "gray" },
  LOW: { min: 1, max: 19, label: "Bajo (<20)", color: "green" },
  MEDIUM: { min: 20, max: 49, label: "Medio (20-49)", color: "yellow" },
  HIGH: { min: 50, max: 99, label: "Alto (50-99)", color: "orange" },
  VERY_HIGH: { min: 100, label: "Muy Alto (≥100)", color: "red" },
};

// Componente para mostrar todas las rutas
const DynamicRoutes = ({ routes }) => {
  // Memorizar los puntos para evitar recálculos innecesarios
  const points = useMemo(() => {
    if (!Array.isArray(routes)) return [];

    return routes.map((route) => ({
      position: [route.start_lat, route.start_lng],
      count: route.count,
      unique_vehicles: route.unique_vehicles,
    }));
  }, [routes]);

  // Memorizar la función de creación de iconos
  const createClusterIcon = useMemo(() => {
    return (cluster) => {
      const count = cluster
        .getAllChildMarkers()
        .reduce((acc, marker) => acc + marker.options.count, 0);
      const size = count < 100 ? 40 : count < 1000 ? 50 : 60;

      return L.divIcon({
        html: `
          <div class="cluster-icon" style="
            background-color: ${
              count >= 1000 ? "#ef4444" : count >= 100 ? "#f97316" : "#22c55e"
            };
            width: ${size}px;
            height: ${size}px;
            line-height: ${size}px;
            font-size: ${count >= 1000 ? "14px" : "12px"};
          ">
            ${count >= 1000 ? `${(count / 1000).toFixed(1)}K` : count}
          </div>
        `,
        className: "custom-cluster-icon",
        iconSize: L.point(size, size),
      });
    };
  }, []);

  // Memorizar las opciones del cluster
  const clusterOptions = useMemo(
    () => ({
      chunkedLoading: true,
      maxClusterRadius: (zoom) => {
        if (zoom >= 13) return 40;
        if (zoom >= 10) return 60;
        return 80;
      },
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      removeOutsideVisibleBounds: true,
      iconCreateFunction: createClusterIcon,
    }),
    [createClusterIcon]
  );

  if (!Array.isArray(routes) || routes.length === 0) return null;

  return (
    <MarkerClusterGroup {...clusterOptions}>
      {points.map((point, index) => (
        <Marker
          key={`marker-${index}`}
          position={point.position}
          count={point.count}
          icon={L.divIcon({
            html: `
              <div class="route-marker" style="
                background-color: ${
                  point.count >= 100
                    ? "#ef4444"
                    : point.count >= 50
                    ? "#f97316"
                    : "#22c55e"
                };
              ">
                ${
                  point.count >= 1000
                    ? `${(point.count / 1000).toFixed(1)}K`
                    : point.count
                }
              </div>
            `,
            className: "custom-route-marker",
            iconSize: [30, 30],
          })}
        >
          <Tooltip>
            <Box p={2}>
              <Text fontWeight="bold">
                Total: {point.count.toLocaleString()}
              </Text>
              <Text fontWeight="bold">
                Únicos: {point.unique_vehicles.toLocaleString()}
              </Text>
            </Box>
          </Tooltip>
        </Marker>
      ))}
    </MarkerClusterGroup>
  );
};

const TrafficMapDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [trafficData, setTrafficData] = useState({
    routes: [],
    stats: {
      totalVehicles: 0,
      totalUniqueVehicles: 0,
    },
    total: 0,
    totalPages: 0,
  });
  const [selectedRange, setSelectedRange] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10000);

  const toast = useToast();

  // Cargar datos de tráfico
  useEffect(() => {
    const fetchTrafficData = async () => {
      try {
        setLoading(true);
        const range = TRAFFIC_RANGES[selectedRange];
        const params = {
          page: currentPage,
          limit: pageSize,
          minCount: range.min,
        };
        if (range.max) {
          params.maxCount = range.max;
        }

        const response = await axiosInstance.get(`/traffic-routes`, { params });
        console.log("Respuesta del backend:", response.data);
        setTrafficData(response.data);
      } catch (error) {
        console.error("Error al cargar datos de tráfico:", error);
        toast({
          title: "Error",
          description: "Error al cargar los datos de tráfico",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTrafficData();
  }, [currentPage, pageSize, selectedRange, toast]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRangeChange = (value) => {
    setSelectedRange(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  return (
    <Box p={5} bg="#f8fafc" minH="100vh">
      <Card bg="white" borderRadius="xl" boxShadow="lg" mb={5}>
        <CardHeader bg="#f1f5f9" borderTopRadius="xl" p={4}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center" wrap="wrap">
              <Heading size="md" color="#2563eb">
                Mapa de Tráfico
              </Heading>
              <HStack spacing={4}>
                <FormControl w="200px">
                  <FormLabel>Rutas por página</FormLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    bg="white"
                    borderColor="#e2e8f0"
                  >
                    <option value="10000">10,000 rutas</option>
                    <option value="20000">20,000 rutas</option>
                    <option value="30000">30,000 rutas</option>
                    <option value="40000">40,000 rutas</option>
                    <option value="50000">50,000 rutas</option>
                  </Select>
                </FormControl>
              </HStack>
            </HStack>
            <RadioGroup onChange={handleRangeChange} value={selectedRange}>
              <HStack spacing={4} wrap="wrap">
                {Object.entries(TRAFFIC_RANGES).map(([key, range]) => (
                  <Radio
                    key={key}
                    value={key}
                    colorScheme={range.color}
                    size="md"
                  >
                    <Badge
                      colorScheme={range.color}
                      fontSize="sm"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      {range.label}
                    </Badge>
                  </Radio>
                ))}
              </HStack>
            </RadioGroup>
          </VStack>
        </CardHeader>
        <CardBody p={4}>
          <StatGroup mb={4} gap={4}>
            <Stat>
              <StatLabel>Total Vehículos</StatLabel>
              <StatNumber>
                {trafficData.stats?.totalVehicles?.toLocaleString() || 0}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Vehículos Únicos</StatLabel>
              <StatNumber>
                {trafficData.stats?.totalUniqueVehicles?.toLocaleString() || 0}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total Rutas</StatLabel>
              <StatNumber>
                {trafficData.total?.toLocaleString() || 0}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Rutas en Vista</StatLabel>
              <StatNumber>
                {trafficData.routes?.length?.toLocaleString() || 0}
              </StatNumber>
            </Stat>
          </StatGroup>

          <Box
            height="600px"
            borderRadius="lg"
            overflow="hidden"
            border="1px solid #e2e8f0"
            mb={4}
          >
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Spinner size="xl" color="#2563eb" />
              </Box>
            ) : (
              <MapContainer
                center={[-1.831239, -78.183406]}
                zoom={7}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <DynamicRoutes routes={trafficData.routes} />
              </MapContainer>
            )}
          </Box>

          {/* Controles de paginación */}
          <HStack justify="center" mb={4}>
            <ButtonGroup>
              <Button
                onClick={() => handlePageChange(1)}
                isDisabled={currentPage === 1}
              >
                Primera
              </Button>
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Anterior
              </Button>
              <Button isDisabled>
                Página {currentPage} de {trafficData.totalPages || 1}
              </Button>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === trafficData.totalPages}
              >
                Siguiente
              </Button>
              <Button
                onClick={() => handlePageChange(trafficData.totalPages)}
                isDisabled={currentPage === trafficData.totalPages}
              >
                Última
              </Button>
            </ButtonGroup>
          </HStack>

          {/* Tabla de datos */}
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Coordenadas Inicio</Th>
                  <Th>Coordenadas Fin</Th>
                  <Th isNumeric>Total Vehículos</Th>
                  <Th isNumeric>Vehículos Únicos</Th>
                  <Th>Fecha Generación</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Array.isArray(trafficData.routes) &&
                  trafficData.routes.map((route, index) => (
                    <Tr key={index}>
                      <Td>{`${route.start_lat}, ${route.start_lng}`}</Td>
                      <Td>{`${route.end_lat}, ${route.end_lng}`}</Td>
                      <Td isNumeric>{route.count.toLocaleString()}</Td>
                      <Td isNumeric>
                        {route.unique_vehicles.toLocaleString()}
                      </Td>
                      <Td>{new Date(route.generated_at).toLocaleString()}</Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </Box>
  );
};

export default TrafficMapDashboard;
